import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const inputCls = 'w-full bg-brand-bg border border-white/10 focus:border-brand-primary text-white px-2 py-2 outline-none text-sm';

const cellOf = (day, mid, data) => {
    const hits = data.bookings.filter((x) => x.machine_id === mid && x.start <= day && day <= x.end);
    const b = hits.find((x) => x.blocking);
    if (b) return { cls: 'bg-brand-primary', title: `${b.request_number} · ${b.name} · Booked`, kind: 'booked' };
    const k = data.blocks.find((x) => x.machine_id === mid && x.start_date <= day && day <= x.end_date);
    if (k) return { cls: 'bg-neutral-500', title: `Blocked${k.reason ? ` · ${k.reason}` : ''}`, kind: 'blocked' };
    const p = hits[0];
    if (p) return { cls: 'bg-brand-primary/30 border border-dashed border-brand-primary', title: `${p.request_number} · ${p.name} · Pending (not blocking)`, kind: 'pending' };
    return null;
};

const BlockForm = ({ machines, onAdded }) => {
    const [f, setF] = useState({ machine_id: '', start_date: '', end_date: '', reason: '' });
    const add = async () => {
        if (!f.machine_id || !f.start_date || !f.end_date) { toast.error('Pick a machine and both dates'); return; }
        try { await api.post('/admin/rental-blocks', f); toast.success('Days blocked'); setF({ ...f, start_date: '', end_date: '', reason: '' }); onAdded(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not block those days'); }
    };
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mt-6" data-testid="cal-block-form">
            <label className="md:col-span-3 text-xs text-white/50">Machine
                <select value={f.machine_id} onChange={upd('machine_id')} className={inputCls} data-testid="cal-block-machine">
                    <option value="">Choose…</option>{machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select></label>
            <label className="md:col-span-2 text-xs text-white/50">From<input type="date" value={f.start_date} onChange={upd('start_date')} className={inputCls} data-testid="cal-block-start" /></label>
            <label className="md:col-span-2 text-xs text-white/50">To<input type="date" value={f.end_date} onChange={upd('end_date')} className={inputCls} data-testid="cal-block-end" /></label>
            <label className="md:col-span-3 text-xs text-white/50">Reason<input value={f.reason} onChange={upd('reason')} placeholder="Maintenance, trade show…" className={inputCls} data-testid="cal-block-reason" /></label>
            <button onClick={add} className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-brand-primary text-brand-ink font-bold px-4 py-2 label-caps text-xs" data-testid="cal-block-add"><Plus className="h-4 w-4" /> Block days</button>
        </div>
    );
};

const RentalCalendarAdmin = () => {
    const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
    const [data, setData] = useState({ machines: [], bookings: [], blocks: [] });
    const nDays = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const days = Array.from({ length: nDays }, (_, i) => iso(new Date(month.getFullYear(), month.getMonth(), i + 1)));
    const load = useCallback(() => api.get('/admin/rental-calendar', { params: { start: iso(month), days: nDays } }).then((r) => setData(r.data)), [month, nDays]);
    useEffect(() => { load(); }, [load]);
    const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
    const del = async (id) => { await api.delete(`/admin/rental-blocks/${id}`); toast.success('Block removed'); load(); };
    const today = iso(new Date());
    const name = (id) => data.machines.find((m) => m.id === id)?.name || 'Machine';

    return (
        <div className="mb-12" data-testid="admin-rental-calendar">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-black uppercase text-3xl">Rental calendar</h2>
                <div className="flex items-center gap-2">
                    <button onClick={() => shift(-1)} className="border border-white/10 p-2 hover:bg-white/10" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="label-caps w-40 text-center" data-testid="cal-month">{month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                    <button onClick={() => shift(1)} className="border border-white/10 p-2 hover:bg-white/10" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
                </div>
            </div>
            <div className="flex gap-4 text-xs text-white/60 mb-3">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-brand-primary" /> Booked (deposit paid or confirmed)</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-brand-primary/30 border border-dashed border-brand-primary" /> Pending request</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-neutral-500" /> Blocked by you</span>
            </div>
            <div className="overflow-x-auto border border-white/10" data-testid="cal-grid">
                <table className="w-full text-xs border-collapse">
                    <thead><tr><th className="sticky left-0 bg-brand-bg text-left p-2 min-w-[160px]">Machine</th>
                        {days.map((d) => <th key={d} className={`p-1 w-7 font-normal ${d === today ? 'text-brand-primary font-bold' : 'text-white/50'}`}>{Number(d.slice(8))}</th>)}</tr></thead>
                    <tbody>
                        {data.machines.length === 0 && <tr><td colSpan={days.length + 1} className="p-4 text-white/50">No machines are set up for rent yet.</td></tr>}
                        {data.machines.map((m) => (
                            <tr key={m.id} className="border-t border-white/10" data-testid={`cal-row-${m.id}`}>
                                <td className="sticky left-0 bg-brand-bg p-2 text-white truncate max-w-[200px]">{m.name}</td>
                                {days.map((d) => { const c = cellOf(d, m.id, data); return (
                                    <td key={d} className="p-0.5"><div title={c?.title || ''} data-testid={`cal-cell-${m.id}-${d}`} data-kind={c?.kind || 'free'} className={`h-6 ${c ? c.cls : 'bg-white/5'}`} /></td>
                                ); })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <BlockForm machines={data.machines} onAdded={load} />
            {data.blocks.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="cal-blocks">
                    {data.blocks.map((b) => (
                        <div key={b.id} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm" data-testid={`cal-block-${b.id}`}>
                            <span className="text-white/80">{name(b.machine_id)} · {b.start_date} → {b.end_date}{b.reason ? ` · ${b.reason}` : ''}</span>
                            <button onClick={() => del(b.id)} className="text-white/50 hover:text-red-400" data-testid={`cal-block-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RentalCalendarAdmin;
